import { Delete, PlusOne } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import React from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { getClients } from "services/client";

export interface Credit {
  client_id: string;
  amount: number;
}

export interface DailyCredits {
  day: string;
  credit_transactions: Credit[];
}

export const DailyCreditsCreate: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Get values from URL parameters
  const dateFromUrl = searchParams.get('date');
  const nbrLinesFromUrl = searchParams.get('nbrLines');

  const {
    saveButtonProps,
    refineCore: { formLoading },
    control,
    watch,
    formState: { errors },
  } = useForm<DailyCredits>({
    defaultValues: {
      day: dateFromUrl 
        ? new Date(dateFromUrl).toISOString().split("T")[0] // Ensure it's formatted correctly
        : new Date().toISOString().split("T")[0], 
        credit_transactions: Array(Number(nbrLinesFromUrl) || 1).fill({ client_id: null, amount: null }),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "credit_transactions",
  });

  const { data: dataClients } = useQuery({
    queryKey: "clients",
    queryFn: getClients(),
  });

  const formValues = watch();

  console.log({formValues});
  

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Controller
        name="day"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            variant="outlined"
            size="small"
            label="Day"
            type="date"
            sx={{ mb: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
      <Stack direction="column" alignItems="flex-start" spacing={2}>
        {(fields || []).map((item, index) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <Controller
              name={`credit_transactions.${index}.client_id`}
              control={control}
              render={({ field }) => (
                <Autocomplete
                  value={dataClients?.find(
                    (client: { id: string; name: string }) =>
                      client.id === field.value
                  )}
                  options={
                    Array.isArray(dataClients)
                      ? dataClients?.filter(
                          (item: any) =>
                            !formValues?.credit_transactions
                              ?.map((item: any) => item?.client_id)
                              .includes(item?.id)
                        )
                      : []
                  }
                  getOptionLabel={(option: any) => `${option?.name} ${option?.pseudo ? `(${option?.pseudo})` : ""}` || ""}
                  onChange={(_, value) => field.onChange(value ? value.id : "")}
                  sx={{
                    width: "400px",
                    marginRight: "8px",
                  }}
                  renderOption={(props, option: any) => (
                    <li {...props}>
                      <Typography variant="body2" fontWeight={500} sx={{  p: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: option?.color,
                        }}/>
                       <span>{`${option?.name} ${option?.pseudo ? `(${option?.pseudo})` : ""}`}</span>
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      variant="outlined"
                      size="small"
                      error={!dataClients}
                    />
                  )}
                />
              )}
            />

            <Controller
              name={`credit_transactions.${index}.amount`}
              control={control}
              rules={{
                required: "Amount is required",
                min: {
                  value: 0.01,
                  message: "Amount must be greater than 0"
                },
                validate: (value) => {
                  if (value <= 0) {
                    return "Amount must be greater than 0";
                  }
                  return true;
                }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  size="small"
                  label="Amount"
                  type="number"
                  inputProps={{
                    min: 0.01,
                    step: 0.01
                  }}
                  sx={{ marginRight: "8px" }}
                  error={!!(errors as any)?.credit_transactions?.[index]?.amount}
                  helperText={(errors as any)?.credit_transactions?.[index]?.amount?.message}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    // Prevent negative values by setting minimum to 0.01
                    if (value < 0) {
                      field.onChange(0.01);
                    } else {
                      field.onChange(value || '');
                    }
                  }}
                />
              )}
            />

            <IconButton onClick={() => remove(index)}>
              <Delete />
            </IconButton>
          </Box>
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        width="34%"
        justifyContent="space-between"
        mt={4}
      >
        <Button
          type="button"
          onClick={() => append({ client_id: null, amount: 0.01 })}
          startIcon={<PlusOne />}
        >
          Add Client Amount
        </Button>
      </Stack>
    </Create>
  );
};